// Set functionality for responsive table.
// when the table is wider then the parent (with class .rte), then it becomes responsive.
// the following is needed:
// * The first column will become sticky
// * The rest become scrollable
// * We add buttons underneath the table that will scroll to the correct column (1 button for every column in the table)

const selectors = {
    table: 'table',
    container: 'table-wrapper',
};
const classes = {
    active: 'active',
    responsiveTable: 'responsive-table',
    scrollButtons: 'button-wrapper',
    stickyColumn: 'sticky-column',
};

export default class ResponsiveTable {
    constructor() {
        this.tables = document.querySelectorAll(selectors.table);
        this.scrollTriggeredByButton = false;
        this._init();
    }

    _addScrollButtons(table) {
        const columns = table.querySelectorAll('tbody tr:first-child th, tbody tr:first-child td');
        const buttonContainer = document.createElement('div');
        buttonContainer.classList.add(classes.scrollButtons, 'u-sticky-left', 'my-4');

        columns.forEach((col, index) => {
            if (index === 0) return;

            const button = document.createElement('button');
            button.classList.add('button', 'button--pill');
            button.dataset.columnIndex = index;
            button.innerHTML = `<span class="sr-only">Scroll to Column ${index + 1}</span>`;
            button.addEventListener('click', () => {
                this.scrollTriggeredByButton = true;
                this._resetButtons(buttonContainer);
                button.classList.add(classes.active);
                this._scrollToColumn(table, index);
            });
            buttonContainer.appendChild(button);
        });

        table.after(buttonContainer);
    }

    _addScrollEvents(table, parent) {
        parent.addEventListener('scroll', () => {
            if (!this.scrollTriggeredByButton) {
                this._highlightScrollButton(table, parent);
            }
        });
    }

    _highlightScrollButton(table, parent) {
        const buttons = parent.querySelectorAll(`.${classes.scrollButtons} button`);
        const columnWidths = Array.from(
            table.querySelectorAll('tr:first-child th, tr:first-child td')
        ).map((cell) => cell.offsetWidth);
        const scrollLeft =
            parent.scrollLeft + table.querySelector(`.${classes.stickyColumn}`).offsetWidth;

        let cumulativeWidth = 0;
        let highlightedIndex = -1;

        for (let i = 0; i < columnWidths.length; i++) {
            cumulativeWidth += columnWidths[i];
            if (cumulativeWidth > scrollLeft) {
                highlightedIndex = i;
                break;
            }
        }

        buttons.forEach((button) => {
            if (parseInt(button.dataset.columnIndex, 10) === highlightedIndex) {
                button.classList.add(classes.active);
            } else {
                button.classList.remove(classes.active);
            }
        });
    }

    _resetButtons(container) {
        const activeButtons = container.querySelectorAll(`button.${classes.active}`);
        activeButtons.forEach((button) => {
            button.classList.remove(classes.active);
        });
    }

    _scrollToColumn(table, index) {
        const stickyWidth = table.querySelector(`.${classes.stickyColumn}`).offsetWidth;
        const cells = table.querySelectorAll(`tr > *:nth-child(${index + 1})`);
        if (cells.length > 0) {
            const cell = cells[index];
            const cellLeft = cell.offsetLeft;
            const scrollPosition = cellLeft - stickyWidth;
            table.parentElement.scrollTo({ left: scrollPosition, behavior: 'smooth' });

            // Set a timeout to reset the scrollTriggeredByButton flag after the scroll animation
            setTimeout(() => {
                this.scrollTriggeredByButton = false;
            }, 500); // Adjust the timeout duration if needed
        }
    }

    _makeFirstColumnSticky(table) {
        const firstColCells = table.querySelectorAll('tr > *:first-child');
        firstColCells.forEach((cell) => {
            cell.classList.add(classes.stickyColumn);
        });
    }

    checkTables() {
        this.tables.forEach((table) => {
            // Check if wrapper already exists, else create new wrapper.
            let wrapper = table.parentElement;
            if (!wrapper.classList.contains(selectors.container)) {
                wrapper = document.createElement('div');
                wrapper.classList.add(selectors.container);

                table.replaceWith(wrapper);
                wrapper.appendChild(table);
            }

            const parent = table.closest(`.${selectors.container}`);

            //If table is wider than parent, make table responsive
            if (table.offsetWidth > parent.offsetWidth) {
                this.makeResponsive(table, parent);
            } else {
                this.removeResponsive(table, parent);
            }
        });
    }

    makeResponsive(table, parent) {
        if (!table.classList.contains(classes.responsiveTable)) {
            table.classList.add(classes.responsiveTable);
            this._makeFirstColumnSticky(table);
            this._addScrollButtons(table);
            this._addScrollEvents(table, parent);
        }
    }

    removeResponsive(table, parent) {
        if (table.classList.contains(classes.responsiveTable)) {
            table.classList.remove(classes.responsiveTable);
            table
                .querySelectorAll(`.${classes.stickyColumn}`)
                .forEach((cell) => cell.classList.remove(classes.stickyColumn));
            const buttonContainer = parent.querySelector(`.${classes.scrollButtons}`);
            if (buttonContainer) {
                buttonContainer.remove();
            }
        }
    }

    _init() {
        window.addEventListener('load', () => this.checkTables());
        window.addEventListener('resize', () => this.checkTables());

        this.checkTables();
    }
}

document.addEventListener('DOMContentLoaded', () => {
    new ResponsiveTable('table');
});
